import type { CadenceThreadGraphNode, EmailThreadGraphNode } from '@poseidon/shared-utils/cadenceStep';
import {
  blankSubjectToNull,
  findFirstEmailChild,
  findPreviousEmailStep,
  resolveCadenceThreadSubject,
} from '@poseidon/shared-utils/cadenceStep';

import type { PreviousEmailThread } from '../ContactDetail/previousEmailThread';

// The walk itself (`findPreviousEmailStep`) and the per-contact rule (`resolveCadenceThreadSubject`)
// live in shared-utils: the server reads the SAME rule to decide whether a thread-continuing step's
// (hidden) subject is checked, so the editor and the server can't drift apart.

/**
 * Where a NEW step (not yet in the graph) is being inserted — the same anchor the create call sends.
 */
export type NewStepAnchor = { afterStepId?: string | null; beforeStepId?: string | null };

/**
 * The email a NEW email step would continue, based on where it's being inserted — mirroring
 * {@link findPreviousEmailStep}, reasoning from the anchor the way `cadenceTemplateAddStep` /
 * `cadenceAddStep` parent the new step:
 * - `beforeStepId` (a delay / wait_for_connect): the new day takes that target's slot, so it
 *   continues whatever the target itself inherited.
 * - `afterStepId` pointing at a delay: a new day hangs off it, so it continues that day's email.
 * - `afterStepId` pointing at a branch node: the new step joins an arm, which continues whatever
 *   ran before the branch.
 * - `afterStepId` pointing at a runnable step: the new email joins that step's day, so — like any
 *   same-day email — it continues the previous day's email, not the anchor.
 */
export function findPreviousEmailStepForNewStep<N extends EmailThreadGraphNode>(
  nodes: Readonly<Record<string, N>>,
  anchor: NewStepAnchor,
): N | null {
  if (anchor.beforeStepId != null) return findPreviousEmailStep(nodes, anchor.beforeStepId);

  const afterId = anchor.afterStepId;
  if (afterId == null) return null;
  const after = nodes[afterId];
  if (after == null) return null;

  if (after.type === 'delay') return findFirstEmailChild(nodes, afterId) ?? findPreviousEmailStep(nodes, afterId);
  if (after.type === 'wait_for_connect' || after.type === 'switch') return findPreviousEmailStep(nodes, afterId);
  // Same-day append: the new email inherits exactly what an email already in that day does.
  return after.parent_id != null ? findPreviousEmailStep(nodes, after.parent_id) : null;
}

/**
 * TEMPLATE editors: subject of the email step that runs before `nodeId`, or null when there's none.
 * Drives the "continue previous thread" toggle: it only shows when an earlier email step exists, and
 * the returned subject is what a thread-continuing step inherits. A blank subject means there's no
 * thread subject to inherit (matches the server), so it's treated as no match.
 */
export function findPreviousEmailSubject(
  nodes: Readonly<Record<string, EmailThreadGraphNode>>,
  nodeId: string,
): string | null {
  return blankSubjectToNull(findPreviousEmailStep(nodes, nodeId)?.subject);
}

/** TEMPLATE editors: {@link findPreviousEmailSubject} for a step that's still being created. */
export function findPreviousEmailSubjectForNewStep(
  nodes: Readonly<Record<string, EmailThreadGraphNode>>,
  anchor: NewStepAnchor,
): string | null {
  return blankSubjectToNull(findPreviousEmailStepForNewStep(nodes, anchor)?.subject);
}

/**
 * PER-CONTACT (cadence) editors: the subject the email step `stepId` would continue, or null when
 * it has no thread to join — which hides the editor's Thread toggle.
 *
 * @param emailThread The cadence OWNER's latest email with the contact (`getPreviousEmailThread`
 *   scoped to the cadence's user — that's whose mailbox the step sends from), or null.
 */
export function findCadenceThreadSubject(
  nodes: Readonly<Record<string, CadenceThreadGraphNode>>,
  stepId: string,
  emailThread: PreviousEmailThread | null,
): string | null {
  return resolveCadenceThreadSubject(findPreviousEmailStep(nodes, stepId), emailThread);
}

/** PER-CONTACT editors: {@link findCadenceThreadSubject} for a step that's still being created. */
export function findCadenceThreadSubjectForNewStep(
  nodes: Readonly<Record<string, CadenceThreadGraphNode>>,
  anchor: NewStepAnchor,
  emailThread: PreviousEmailThread | null,
): string | null {
  return resolveCadenceThreadSubject(findPreviousEmailStepForNewStep(nodes, anchor), emailThread);
}
